import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { BasketTotalsComponentDef, IBasketTotalsItem } from '@df/basket/view/basket-totals.component.def';
import { MultisiteService } from '@df/core/service/multisite';
import { Status } from '@df/core/status';
import { Giftcards } from '@df/module-giftcards/giftcards';
import { LineHeightByComponent } from '@df/ui/common/line-height-by.component';
import { ValuesPipe } from '@df/ui/common/values.pipe';
import { PriceComponent } from '@df/ui/price/price.component';
import { TaxMode } from '@df/ui/price/price.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { CheckoutFormComponent } from '../../checkout/checkout-form.component';
import { InlineButtonComponent } from '../../ui/buttons/inline-button/inline-button.component';
import { TooltipComponent } from '../../ui/common/tooltip/tooltip.component';

declare module '@df/basket/view/basket-totals.component.def' {
    interface IBasketTotalsItem {
        hideCurrency?: boolean;
    }
}

@Component({
    selector: 'basket-view-totals',
    templateUrl: 'basket-view-totals.html',
    standalone: true,
    imports: [NgClass, TranslationModule, InlineButtonComponent, PriceComponent, LineHeightByComponent, TooltipComponent, ValuesPipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BasketViewTotalsComponent extends BasketTotalsComponentDef {
    /**
     * Allow the removal of discounts from the basket
     */
    @Input()
    allowDiscountRemoval = false;

    @Input()
    showSurchargeInfo = false;

    /**
     * Giftcards removal status map
     */
    private giftcardStatusMap = new Map<string, Status>();
    /**
     * Status model for the remove discount action
     */
    protected removeDiscountStatus = new Status();
    private multisiteService = inject(MultisiteService);
    private checkoutFormComponent = inject(CheckoutFormComponent, { optional: true });

    private isUs = this.multisiteService.current.siteId === 'us';

    protected override setSubscriptions(): void {
        super.setSubscriptions();
        if (this.checkoutFormComponent) {
            this.subscriptions.add(
                this.checkoutFormComponent.checkoutBasketData$.subscribe(() => {
                    this.update();
                })
            );
        }
    }

    protected override getSubtotalLabel(taxMode?: TaxMode): string {
        if (this.isUs) {
            return 'Subtotal';
        }
        return taxMode === TaxMode.excluding ? 'Subtotal (excl. VAT)' : 'Subtotal';
    }

    protected override getTotalLabel(): string {
        if (this.isUs) {
            return 'Total';
        }
        return this.withShipping ? 'Order total' : 'Order total (Excl. delivery)';
    }

    /**
     * Remove the discount from the basket
     */
    protected removeDiscount(): void {
        this.basket.removeCoupon(this.removeDiscountStatus, {
            coupon: this.basket.data.coupon,
            isCheckout: !!this.checkoutFormComponent
        });
    }
    protected getGiftcardStatus(giftcard: Giftcards.IAddedGiftcardData) {
        const code = giftcard.code;
        if (!this.giftcardStatusMap.has(code)) {
            this.giftcardStatusMap.set(code, new Status());
        }
        return this.giftcardStatusMap.get(code);
    }
    protected removeGiftCard(giftcard: Giftcards.IAddedGiftcardData): Promise<any> | void {
        const code = giftcard.code;
        const checkout = !!this.checkoutFormComponent;
        const data: Giftcards.IRemoveData = { code, checkout };
        return this.requestService.send(this.getGiftcardStatus(giftcard), {
            path: checkout ? Giftcards.Endpoints.CheckoutRemove : Giftcards.Endpoints.Remove,
            data
        });
    }
    protected getGiftcardLabel(giftcard: Giftcards.IAddedGiftcardData): string {
        const code = giftcard.code;
        // returns last 4 characters of code
        return code && `...${code.substring(code.length - 4)}`;
    }
    protected trackGiftCard(index: number, giftcard: Giftcards.IAddedGiftcardData): string {
        return giftcard.code;
    }

    protected totalClass(itemId: string, first: boolean): string {
        const cssClasses = {
            subtotal: 'p1 col-12 m-t-2',
            total: first ? 's1 col-1 m-t-3 p-t-3' : 'b-t b-col-42 s1 col-1 m-t-3 p-t-3'
        };
        return cssClasses[itemId] || 'p1 col-1 m-t-2';
    }

    protected override setTotals(totals: IBasketTotalsItem[]): void {
        if (this.isUs && !this.withShipping) {
            const taxTotal = totals.find(total => total.id === 'tax');
            if (taxTotal) {
                totals = totals.filter(total => total !== taxTotal);
                const grandTodal = totals.find(total => total.id === 'total');
                if (grandTodal) {
                    grandTodal.value -= taxTotal.value;
                }
            }
        }
        return super.setTotals(totals);
    }

    protected override addCustomTotals(totals: IBasketTotalsItem[], totalsMap: Record<string, number>): void {
        const rewardPoints = this.basket.getTotal('reward_points', 'reward_points');
        if (rewardPoints > 0 && !!totals.length) {
            totals.splice(1, 0, { label: 'Rewards Coins', id: 'reward_points', value: rewardPoints, hideCurrency: true });
            totalsMap['reward_points'] = rewardPoints;
        }
        return super.addCustomTotals(totals, totalsMap);
    }

    protected get info() {
        if (this.isUs && !this.withShipping) {
            return 'Excludes Shipping and Tax';
        }
        return undefined;
    }

    protected override get withShipping(): boolean {
        if (this.checkoutFormComponent) {
            return !!this.checkoutFormComponent.hasShippingMethod;
        }
        return !!this.basket.data?.shippingMethod || !!this.basket.shipping;
    }
}
